/**
 * pipelineDonutView.ts — the pure half of the Sales Home pipeline donut
 * (redesign slice 3, the Minimal board's ring; ADOPTION.md §15; Home tabs
 * H-3a: by VALUE, RULED 2026-10-18).
 *
 * The wire is the server's fixed 5-row spine (open stages + closed_won,
 * zero-filled) that funnelRows already models, plus — since H-3a — the
 * /stats per-stage VALUE the summary already carries (dealsByStage, pence).
 * The fills are BOUND by the spine's fixed position BEFORE any zero slice is
 * dropped (CLAUDE.md chart gotcha: "bind to each category's fixed palette
 * slot BEFORE zero slices are dropped, or the palette reshuffles as data
 * populates") — funnelRows binds them, this file only reads them. The four
 * open rows are a STOCK (what sits in each stage now — C-6), which is what a
 * part-of-whole ring can show honestly; Closed Won stays out of the ring as
 * the all-time flow it is (C-7) and rides beneath it as a tile.
 *
 * H-3a: the arcs are sized by the stage's open VALUE (spec §3.2, "a donut by
 * value in stage colours"), the legend row reads "Proposal sent · 1 · £6.0M"
 * (count AND value), and the centre is the fenced open Σ over the open count.
 * Without a values list (a caller that has none) the ring falls back to the
 * count-sized arcs it drew before, labels sentence-cased either way (R-1,
 * Home-local: the DealStage value and its colour binding never change).
 *
 * Geometry: the boards' 132 px ring. A 2 px gap in the SURFACE colour
 * separates touching arcs (the dataviz mark spec) — the ring draws arcs only,
 * so the card shows through the gaps; an all-zero pipeline draws one track.
 */
import { funnelRows, CLOSED_WON_STAGE, type FunnelCount, type FunnelRow } from './funnelRows'
import { stageDisplay } from './homeView'

export const RING = { box: 132, radius: 54, stroke: 14, gap: 2 } as const
const C = 2 * Math.PI * RING.radius

/** The summary's dealsByStage row: the stage LABEL (SLUG_TO_STAGE), its count
 *  and its fenced value in pence. */
export interface StageValue { stage: string; count: number; value: number }

export interface DonutArc {
  stage: string; label: string; display: string; count: number; value: number | null; fill: string
  /** of the ring's total (value when by value, else count), 0–100 */
  pct: number
  /** SVG stroke geometry for a circle of RING.radius */
  dashArray: string; dashOffset: number
  /** the hover title: "Proposal sent · 1 · £6.0M" (by value) / "New lead · 4 of 9" */
  title: string
}

export interface DonutModel {
  openTotal: number
  /** the fenced open Σ in pence when sized by value; null when by count */
  openValuePence: number | null
  byValue: boolean
  /** every open stage, zero rows INCLUDED, in spine order, fill bound */
  legend: { stage: string; label: string; display: string; count: number; value: number | null; fill: string }[]
  /** only the non-zero stages, drawn in spine order */
  arcs: DonutArc[]
  won: { stage: string; label: string; display: string; count: number; value: number | null } | null
}

export function donutModel(funnel: FunnelCount[], values?: StageValue[], fmt?: (pence: number) => string): DonutModel {
  const rows = funnelRows(funnel)
  const byValue = !!values && values.length > 0
  const byLabel = new Map((values ?? []).map(v => [v.stage, v.value]))
  const valueOf = (label: string): number | null => (byValue ? (byLabel.get(label) ?? 0) : null)
  const open = rows.filter((r): r is Extract<FunnelRow, { kind: 'open' }> => r.kind === 'open')
  const wonRow = rows.find((r): r is Extract<FunnelRow, { kind: 'won' }> => r.kind === 'won')
  const openTotal = open.reduce((s, r) => s + r.count, 0)
  const openValuePence = byValue ? open.reduce((s, r) => s + (valueOf(r.label) ?? 0), 0) : null
  const weightOf = (r: Extract<FunnelRow, { kind: 'open' }>) => (byValue ? (valueOf(r.label) ?? 0) : r.count)
  const total = byValue ? (openValuePence ?? 0) : openTotal
  const legend = open.map(r => ({ stage: r.stage, label: r.label, display: stageDisplay(r.label), count: r.count, value: valueOf(r.label), fill: r.fill }))
  const arcs: DonutArc[] = []
  let offset = 0
  for (const r of open) {
    const w = weightOf(r)
    if (w <= 0 || total <= 0) continue
    const len = (w / total) * C
    const drawn = Math.max(0, len - RING.gap)
    const display = stageDisplay(r.label)
    const value = valueOf(r.label)
    arcs.push({
      stage: r.stage, label: r.label, display, count: r.count, value, fill: r.fill, pct: Math.round((w / total) * 100),
      dashArray: `${drawn} ${C - drawn}`, dashOffset: -offset,
      title: byValue && fmt && value != null ? `${display} · ${r.count} · ${fmt(value)}` : `${display} · ${r.count} of ${openTotal}`,
    })
    offset += len
  }
  return {
    openTotal, openValuePence, byValue, legend, arcs,
    won: wonRow ? { stage: wonRow.stage, label: wonRow.label, display: stageDisplay(wonRow.label), count: wonRow.count, value: valueOf(wonRow.label) } : null,
  }
}

export { CLOSED_WON_STAGE }
